import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Alert, Button, Glyphicon } from 'react-bootstrap';
import { SwaggerUI, Status, PageHeader } from 'axway-react-ui';
import Breadcrumbs from 'components/widgets/Breadcrumbs';
import Icon from 'components/widgets/Icon';
import browserHistory from 'components/utility/history';
import EndpointView from './EndpointView';

function formatEditFlowLink(flow) {
	return `${window.$conf.prefix}/project/flows/${encodeURIComponent(flow)}/edit`;
}

function formatCreateFlowLink(endpointId, method, path) {
	return `${window.$conf.prefix}/project/flows/create?group=${encodeURIComponent(endpointId)}&method=${encodeURIComponent(method.toLowerCase())}&path=${encodeURIComponent(path)}`;
}

class EndpointsView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.renderDetails = this.renderDetails.bind(this);
		this.confirmDeleteEndpoint = this.confirmDeleteEndpoint.bind(this);
		this.onTestAPIExecute = this.onTestAPIExecute.bind(this);
		this.onTestAPICancel = this.onTestAPICancel.bind(this);
		this.onTestAPIClear = this.onTestAPIClear.bind(this);
		this.viewFlow = this.viewFlow.bind(this);
		this.createFlow = this.createFlow.bind(this);
	}

	onTestAPIExecute(verb, path, request) {
		const { endpointId } = this.props;
		this.props.onTestAPIExecute(endpointId, verb, path, request);
	}

	onTestAPICancel(verb, path) {
		const { endpointId } = this.props;
		this.props.onTestAPICancel(endpointId, verb, path);
	}

	onTestAPIClear(verb, path) {
		const { endpointId } = this.props;
		this.props.onTestAPIClear(endpointId, verb, path);
	}

	renderDetails(method, verb, path) {
		if (!verb || !path) {
			return null;
		}
		const methodId = `${verb}_${path}`;
		let methodSwagger = this.props.swagger && this.props.swagger.paths
			&& this.props.swagger.paths[path] && this.props.swagger.paths[path][verb.toLowerCase()];

		// Swagger isn't generated for erroneous endpoints so lets at least insert the endpoint
		if (!methodSwagger) {
			methodSwagger = this.props.endpoint
				&& this.props.endpoint.paths
				&& this.props.endpoint.paths[path]
				&& this.props.endpoint.paths[path][verb.toLowerCase()];
		}

		if (!methodSwagger) {
			return null;
		}

		return (
			<EndpointView
				endpoint={this.props.swagger}
				method={methodSwagger}
				verb={verb}
				path={path}
				prefix={`${this.props.swagger.basePath || ''}`}
				testmethod={this.props.testapi && this.props.testapi[methodId]}
				project={this.props.project}
				onTestAPIExecute={this.onTestAPIExecute}
				onTestAPICancel={this.onTestAPICancel}
				onTestAPIClear={this.onTestAPIClear}
			/>
		);
	}

	confirmDeleteEndpoint(ev, item) {
		ev.stopPropagation();
		this.props.confirmDeleteEndpoint(item);
	}

	createFlow(ev, endpointId, method, path) {
		ev.stopPropagation();
		ev.preventDefault();
		browserHistory.push(formatCreateFlowLink(endpointId, method, path));
	}

	viewFlow(ev, flow) {
		ev.stopPropagation();
		ev.preventDefault();
		browserHistory.push(formatEditFlowLink(flow));
	}

	render() {
		const { endpoint, swagger, endpointId, url } = this.props;

		const xenabled = endpoint.hasOwnProperty('x-enabled') && endpoint['x-enabled'];
		const validEndpoint = !xenabled || !xenabled.errors || (xenabled.errors.length === 0);
		const warningEndpoint = xenabled && xenabled.warnings && xenabled.warnings.length > 0;
		const disabledEndpoint = !validEndpoint || (xenabled && !xenabled.enabled);

		const endpointActionColumn = {
			key: 'test',
			className: 'nowrap delete',
			format: (item) => {
				return (
					<a
						onClick={(e) => this.confirmDeleteEndpoint(e, item)}
						className="glyphicon glyphicon-trash"
					/>
				);
			}
		};
		const statusColumn = {
			key: 'status',
			className: 'nowrap status',
			name: this.intl.formatMsg('Status'),
			format: (item) => {
				const ep = item.endpoint;
				const flowID = ep['x-flow'];
				const xenabledMethod = ep['x-enabled'];

				if (!xenabledMethod || xenabledMethod.enabled) {
					return (
						<Status type="enabled" />
					);
				} else if (!xenabledMethod.enabled) {
					if (xenabledMethod.errors && xenabledMethod.errors.length) {
						return (
							<Status
								type="error"
								tooltipPlacement="left"
								tooltip={this.intl.formatMsg('There was an error with this method. Please check the logs.')}
							/>
						);
					} else if (!flowID) {
						return (
							<Status
								type="disabled"
								tooltipPlacement="left"
								tooltip={this.intl.formatMsg('Click "Create Flow" to design a flow and enable this method.')}
							/>
						);
					} else {
						return (
							<Status
								type="disabled"
								tooltipPlacement="bottom"
								tooltip={this.intl.formatMsg('Disabled at source')}
							/>
						);
					}
				}
			}
		};
		const flowButtonColumn = {
			key: 'flow',
			className: 'nowrap flow',
			format: (item) => {
				const ep = item.endpoint;
				const flowID = ep['x-flow'];
				if (ep['x-flow']) {
					return (
						<a
							href={formatEditFlowLink(flowID)}
							onClick={(e) => this.viewFlow(e, flowID)}
						>
							<div>
								<Glyphicon glyph=" gg icon-GG-arrow-wave2" />
								{this.intl.formatMsg('Flow')}
							</div>
						</a>
					);
				}
				return (
					<a
						href={formatCreateFlowLink(endpointId, item.method, item.path)}
						onClick={ev => this.createFlow(ev, endpointId, item.method, item.path)}
					>
						{this.intl.formatMsg('Create Flow')}
					</a>
				);
			}
		};

		return (
			<div className="endpointsview">
				<PageHeader>
					<PageHeader.Section align="left">
						<Breadcrumbs
							crumbs={[
								{
									path: `${window.$conf.prefix}/project/apidocs`,
									name: this.intl.formatMsg('API Doc & Test')
								},
								{ name: endpoint.info.title }
							]}
						/>
					</PageHeader.Section>
					<PageHeader.Section align="right">
						<Button
							key="compose ux-item-spacing-s-left"
							className="compact"
							bsSize="large"
							href={url}
							disabled={!validEndpoint || disabledEndpoint}
							download
						>
							<Icon src="icon-GG-download2" />
							{this.intl.formatMsg('Download')}
						</Button>
					</PageHeader.Section>
				</PageHeader>
				<SwaggerUI>
					<SwaggerUI.Info doc={endpoint} />
					{!validEndpoint && (
						<Alert bsStyle="danger">
							<Glyphicon glyph=" gg icon-GG-warning" />
							<span className="message">
								{this.intl.formatMsg('There was an error loading this API. Please check the logs.')}
							</span>
						</Alert>
					)}
					{validEndpoint && disabledEndpoint && (
						<Alert className="alert-neutral">
							<Glyphicon glyph=" gg icon-GG-lock" />
							<span className="message">
								{this.intl.formatMsg('This API is disabled.')}
							</span>
						</Alert>
					)}
					{validEndpoint && !disabledEndpoint && warningEndpoint && (
						<Alert bsStyle="warning">
							<Glyphicon glyph=" gg icon-GG-warning" />
							<span className="message">
								{this.intl.formatMsg('This API has some errors.')}
							</span>
						</Alert>
					)}
					<SwaggerUI.Methods
						doc={endpoint}
						prefix={`${this.props.swagger.basePath || ''}`}
						onExpand={this.renderDetails}
						expressPaths
						additionalColumns={[ statusColumn, flowButtonColumn, endpointActionColumn ]}
					/>
					{swagger.definitions && (
						<SwaggerUI.Definitions doc={swagger} expanded={false} />
					)}
				</SwaggerUI>
			</div>
		);
	}
}

EndpointsView.propTypes = {
	/**
	 * API endpoint (swagger document)
	 */
	endpoint: PropTypes.object.isRequired,

	/**
	 * API endpoint (swagger document) (dereferenced)
	 */
	swagger: PropTypes.object.isRequired,

	/**
	 * API doc URL
	 */
	url: PropTypes.string.isRequired,

	/**
	 * API endpoint's ID
	 */
	endpointId: PropTypes.string.isRequired,

	/**
	 * Prefix on which the endpoint is listening. Must have a leading /.
	 */
	prefix(props, propName, componentName) {
		if (typeof props[propName] !== 'string') {
			return new Error(
				'Invalid prop prefix supplied to'
				+ ` \`${componentName}\`. prefix must be a string. Validation failed.`
			);
		} else if (props[propName][0] !== '/') {
			return new Error(
				'Invalid prop prefix supplied to'
				+ ` \`${componentName}\`. prefix must have a leading /. Validation failed.`
			);
		}
	},

	/**
	 * Method to collect endpoint information for delete after confirmation
	 */
	confirmDeleteEndpoint: PropTypes.func.isRequired,
	/**
	  *  The state for the Test API panels indexed by endpoint id and operation (verb & path).
	 */
	testapi: PropTypes.object.isRequired,

	/**
	 * The callback for the test api execution.
	 */
	onTestAPIExecute: PropTypes.func.isRequired,

	/**
	 * The callback for the test api execution cancelation.
	 */
	onTestAPICancel: PropTypes.func.isRequired,

	/**
	 * The callback for the test api state reset.
	 */
	onTestAPIClear: PropTypes.func.isRequired
};

export default injectIntl(EndpointsView);
